
import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import { useDispatch, useSelector } from 'react-redux'
import { Card } from 'react-bootstrap'

import { useAuth } from '../../../Hooks/UseAuth'
import { usePlan } from '../../../Hooks/UsePlan'
import { useQuery } from '../../../Hooks/UseQuery'
import { IContextMenuOption, useContextMenu } from '../../../Hooks/UseContextMenu'
import useTranslation from '../../../Hooks/UseTranslation'
import { useProject } from '../../../Hooks/UseProject'
import { useProgrammation } from '../../../Hooks/UseProgrammation'
import { useProjection } from '@/Hooks/ProjectionProvider'
import { useTeams } from '@/Hooks/UseTeam'

import {
  getCollapsedGroups,
  getGanttItems,
  getNonWorkingDays,
  saveCollapsedGroups,
  IGetItemsListOptions,
  getGroupTasks,
  getGroupFromList,
  getMacroResult,
  filterByUsers,
  filterByColorCodes,
  filterByStatus,
  filterByGroups,
  filterByTypes,
  filterByDate,
  getCollapsedTasks,
  saveCollapsedTasks
} from '../../../Helpers/ProgrammationHelper'
import TeamsHelper from '../../../Helpers/TeamsHelper'
import { maxDate } from '@/Helpers/DateFunctions'

import { resetActualDates, resetBaseLines } from '@/Services/TasksService'

import InterfaceActions from '../../../Redux/Actions/InterfaceActions'
import ProjectsActions from '../../../Redux/Actions/ProjectsActions'
import TasksActions, { resetActualDatesAction, resetBaseLinesAction } from '../../../Redux/Actions/TasksActions'
import {
  moveItemAction,
  setMacroCollapsedGroupsAction,
  setMacroAction,
  setMacroGanttItems,
  setMacroCollapsedTasksAction,
  cleanMacro
} from '../../../Redux/Actions/MacroActions'

import { getStatusName } from '../../../Components/TaskManager/TaskEditorHelper'
import TaskFilters from '../../../Components/TaskFilters/TaskFilters'
import UserAvatar from '../../../Components/UserAvatar/UserAvatar'
import { GanttColumn } from '../../../Components/GantV2/types'
import Gantt from '../../../Components/GantV2/Gantt'
import Icon from '../../../Components/Icon/Icon'
import AddRestrictionToMany from '../../../Components/AddRestrictionToMany/AddRestrictionToMany'
import ReScheduleMany from '../../../Components/ReScheduleMany/ReScheduleMany'
import UpdateDisplayModeMany from '../../../Components/UpdateDisplayModeMany/UpdateDisplayModeMany'
import PdfExportModal from '../../../Components/PdfExportModal/PdfExportModal'
import ProgrammationManager from '../../../Components/ProgrammationManager/ProgrammationManager'
import ProjectLayout from '@/Layouts/MainLayout/ProjectLayout'
import ContentBody from '@/Layouts/MainLayout/components/ContentBody'
import ProgrammationHeader from '../ProgrammationHeader'
import ProgrammationCreateMenu from '../ProgrammationCreateMenu'
import ResponsivePageToolbar from '@/Layouts/MainLayout/components/ResponsivePageToolbar'

const MacroPage = (props: any) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const sidebarRefBtn = useRef<HTMLButtonElement>(null)

  const query = useQuery()
  const task_id = query.get("task_id") || '';

  const { ability, userData } = useAuth()
  const {
    tooManyRestrictions,
    tooManyTasks,
    showTooManyRestrictionsModal,
    showTooManyTasksModal
  } = usePlan()
  const {
    show: showContextMenu,
    hide: hideContextMenu
  } = useContextMenu()

  const { teams, allInternalProfiles } = useTeams()

  const { project_id, project, loadProject, projectLoaded, patchProject } = useProject()
  const { loadProgrammation, programmationLoaded, openItem, createNewTask, createNewGroup } = useProgrammation()
  const { projectedEndDate, extendedNonWorkingDays } = useProjection()
  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const macro = useSelector((state: any) => state.MacroReducer)

  const [ shownTooManyRestrictionsModal, setShownTooManyRestrictionsModal ] = useState(!tooManyRestrictions)

  const { teamsMembersIds, me } = useMemo(() => {

    const users = TeamsHelper.getAllMembersFromProject(teams)
    const usersIds = users.map((user: any) => user._id)

    const me = users.find((user: any) => user._id === userData?.id)

    return { teamsMembers: users, teamsMembersIds: usersIds, me }
  }, [ teams ])

  const nonWorkingDays = useMemo(() => {

    if (!project._id) return []

    return getNonWorkingDays(project.startsAt, project.endsAt, project)
  }, [ project._id ])

  const [ showTaskFilters, setShowTaskFilters ] = useState(false)

  const savedFilters = localStorage.getItem(`${project_id}-macro-filters`)

  const [ filters, setFilters ] = useState(savedFilters ? JSON.parse(savedFilters) : {
    user: '',
    state: '',
    team: '',
    group: '',
    code: '',
    type: '',
    start: '',
    end: '',
  })

  const [ mySettings, setMySettings ] = useState(project?.mySettings)
  const [ showSidebar, setShowSidebar ] = useState(false)

  const [ showAddRestrictionModal, setShowAddRestrictionModal ] = useState(false)
  const [ restrictionTasks, setRestrictionTasks ] = useState<any[]>([])

  const [ showReScheduleModal, setShowReScheduleModal ] = useState(false)
  const [ reScheduleTasks, setReScheduleTasks ] = useState<any[]>([])

  const [ showPdfExportModal, setShowPdfExportModal ] = useState(false)

  const [ columns, setColumns ] = useState<GanttColumn[]>([])

  const loadMacro = async (withFilters?: any) => {

    if (programmation.loadStatus !== 'loaded') return

    const tasks = programmation.tasks.map((task: any) => {

      return {
        ...task,
        responsibles: task.responsibles.map((resp: any) => programmation.users.find((user: any) => user._id === resp)),
        internalProfiles: task.internalProfiles.map((resp: any) => allInternalProfiles.find((profile: any) => profile._id === resp)),
      }
    })

    const options = {
      ...programmation,
      list: JSON.parse(JSON.stringify(programmation.list)),
      tasks: JSON.parse(JSON.stringify(tasks)),
      filters
    }

    let macroResult = getMacroResult(options)

    const newFilters = withFilters || filters

    if (newFilters.team) {

      const teams_ids = newFilters.team.split(',')

      const teamsFound = teams.filter((team: any) => teams_ids.includes(team._id))

      const users_ids: string[] = []

      teamsFound.forEach((team: any) => {

        team.users.forEach((user: any) => {

          if (!users_ids.includes(user._id))
            users_ids.push(user._id)
        })
      })

      macroResult = filterByUsers(macroResult, users_ids)
    } else if (newFilters.user)
      macroResult = filterByUsers(macroResult, newFilters.user.split(','))

    if (newFilters.group) {

      macroResult = filterByGroups(macroResult, newFilters.group.split(','))
    }else if (newFilters.code)
      macroResult = filterByColorCodes(macroResult, newFilters.code.split(','))

    if (newFilters.type)
      macroResult = filterByTypes(macroResult, newFilters.type.split(','))

    if (newFilters.state)
      macroResult = filterByStatus(macroResult, newFilters.state.split(','))

    if (newFilters.start || newFilters.end)
      macroResult = filterByDate(macroResult, newFilters.start, newFilters.end)

    const collapsedGroups = getCollapsedGroups(project_id, 'macro')
    const collapsedTasks = getCollapsedTasks(project_id, 'macro')

    dispatch(setMacroAction(macroResult, project.startsAt, project.endsAt, collapsedGroups, collapsedTasks))
  }

  const hasActiveFilters = () => {

    const values = Object.values(filters)
    const found = values.findIndex(f => f !== '')

    return !!(found >= 0)
  }

  const handleChangeFilters = (filters: any) => {

    localStorage.setItem(`${project_id}-macro-filters`, JSON.stringify(filters))

    loadMacro(filters)
    setFilters(filters)
  }

  const handleToggleCollapse = async (group_id: string) => {

    const collapsedGroups = getCollapsedGroups(project_id, 'macro')

    if (collapsedGroups.includes(group_id)) {

      collapsedGroups.splice(collapsedGroups.indexOf(group_id), 1)
    } else {

      collapsedGroups.push(group_id)
    }

    saveCollapsedGroups(project_id, 'macro', collapsedGroups)

    dispatch(setMacroCollapsedGroupsAction(collapsedGroups))
  }

  const handleToggleCollapseTask = async (task_id: string) => {
    const collapsedTasks = getCollapsedTasks(project_id, 'macro')

    if (collapsedTasks.includes(task_id)) {

      collapsedTasks.splice(collapsedTasks.indexOf(task_id), 1)
    } else {

      collapsedTasks.push(task_id)
    }

    saveCollapsedTasks(project_id, 'macro', collapsedTasks)

    dispatch(setMacroCollapsedTasksAction(collapsedTasks))
  }

  const handleShowWorkflows = (groups: any[], tasks: any[]) => {

    saveCollapsedGroups(project_id, 'macro', groups)
    saveCollapsedTasks(project_id, 'macro', tasks)

    dispatch(setMacroCollapsedGroupsAction(groups))
    dispatch(setMacroCollapsedTasksAction(tasks))
  }

  const handleResetBaseLines = async (baselineId?: string) => {

    const [ error, tasks ] = await resetBaseLines(project_id, baselineId)

    if (error) {

      return
    }

    if (!baselineId) await dispatch(resetBaseLinesAction(tasks))
  }

  const handleResetActualDates = async () => {

    const [ error, tasks ] = await resetActualDates(project_id)

    if (error) {

      return false
    }

    await dispatch(resetActualDatesAction(tasks))

    patchProject({
      mustRecalculateWeeklyProgress: true,
    })
  }

  const handleEditItem = async (item_id: string, editMode?: boolean) => {

    if (!editMode) {

      hideContextMenu()
      return
    }

    openItem(item_id)
  }

  const handleUpdateSetting = async (key: string, value: any) => {

    let field = key

    if (field === 'dayWidth') {

      field = 'ganttMacroColumnSize'
    } else if (field === 'hideNonWorkingDays') {

      field = 'ganttMacroHideNonWorkingDays'
    } else if (field === 'showVerticalBorders') {

      field = 'ganttMacroShowVerticalBorders'
    } else if (field === 'columnsHidden') {

      field = 'ganttHideMacroFields'
    } else if (field === 'divisorPosition') {

      field = 'ganttMacroDivisorPosition'
    }

    await updateSettings({
      ...mySettings,
      [ field ]: value
    })
  }

  const updateSettings = async (settings = mySettings) => {

    let areDifferent = false

    for (const key in settings) {

      if (key !== 'setted' && settings[ key ] !== project.mySettings[ key ]) {

        areDifferent = true
      }
    }

    if (areDifferent) {

      await dispatch(ProjectsActions.updateSettings(project.mySettings._id, settings))
    }

    setMySettings(settings)
  }

  const toggleSidebar = (target?: any) => {

    if (!target) {

      if (showSidebar) updateSettings()

      setShowSidebar(!showSidebar)
    } else {

      if (sidebarRefBtn.current) {

        const isButtonClicked = sidebarRefBtn.current.contains(target) || target.closest?.('.pl4-settings-trigger')

        if (!isButtonClicked) {

          // trigger click because if not mySettings is not updated
          sidebarRefBtn.current.click()
        }
      }
    }
  }

  const handleUpdatePosition = async (action: any) => {

    if (me?.profile_tag === 'observer') return

    dispatch(moveItemAction(project_id, action))
  }

  const canUpdateSelectedItems = (selectedItems: any[]) => {

    const isPl4nner = ability.can('manage', 'all')
    const isAssistant = project.assistants?.includes(userData?.id)

    const isCollaboratorInEveryTask = selectedItems.every((item: any) => item.responsibles?.map((r: any) => r._id).includes(userData?.id))

    return isPl4nner || isAssistant || isCollaboratorInEveryTask
  }

  const canReScheduleSelectedItems = (selectedItems: any[]) => {

    if (!canUpdateSelectedItems(selectedItems)) return false

    return selectedItems.some((item: any) => item.completed) === false
  }

  const handleCompleteMany = async (selectedItems: any[]) => {

    if (!selectedItems.length) return

    const isPlural = selectedItems.length > 1

    const prompt = await Swal.fire({
      title: `${t('MACRO:CompleteManyTitleA')} ${selectedItems.length} ${t('MACRO:CompleteManyTitleB')}${isPlural ? 's' : ''}.`,
      text: `${t(isPlural ? 'MACRO:CompleteManyContentPlural' : 'MACRO:CompleteManyContentSingular')}`,
      input: 'radio',
      inputOptions: {
        scheduled: t('TASK_EDITOR:InputDisplayModeOptionScheduled'),
        actual: t('TASK_EDITOR:InputDisplayModeOptionActual'),
      },
      inputValue: 'scheduled',
      icon: 'question',
      confirmButtonText: t('MACRO:CompleteManyConfirmButton'),
      showCancelButton: true,
      cancelButtonText: t('MACRO:CompleteManyCancelButton'),
      width: '600px',
      willOpen:() => {

        const popup = Swal.getPopup() as HTMLElement | null
        const radioContainer = popup ? popup.querySelector('.swal2-radio') as HTMLElement | null : null
        if (radioContainer) {
          radioContainer.style.display = 'flex'
          radioContainer.style.flexDirection = 'column'
          radioContainer.style.alignItems = 'flex-start'
        }
      }
    })


    if (!prompt.isConfirmed) return

    const tasksIds = selectedItems.map((item: any) => item._id)

    await(dispatch(TasksActions.completeMany(tasksIds, project._id, prompt.value)))
  }

  const handleAddRestrictionToMany = (selectedItems: any[]) => {

    if (!selectedItems.length) return

    setRestrictionTasks(selectedItems)
    setShowAddRestrictionModal(true)
  }

  const handleReScheduleItems = (selectedItems: any[]) => {

    if (!selectedItems.length) return

    setReScheduleTasks(selectedItems)
    setShowReScheduleModal(true)
  }

  const onRestrictionAdded = async () => {

      setShowAddRestrictionModal(false)
  }

  const onReScheduledTasks = async () => {

    setShowReScheduleModal(false)
  }

  const handleGanttRightClick = () => {

    const options: IContextMenuOption[] = []

    options.push({
      label: t('MACRO_CONTEXT_MENU:NewTask'),
      icon: <Icon name="plus" fas fw />,
      action: () => createNewTask(),
    })

    options.push({
      label: t('MACRO_CONTEXT_MENU:NewGroup'),
      icon: <Icon name="plus" fas fw />,
      action: () => createNewGroup(),
    })

    if (options.length) {

      showContextMenu(options)
    }
  }

  const handleItemsRightClick = async (selectedItems: any[]) => {

    if (!selectedItems.length) {

      return
    }

    let isGroup = false
    let isItem = false
    let hasSubTasks = false
    const areManyTasks = selectedItems.length > 1

    let childTasks: any[] = []

    let parent: any

    if (selectedItems.length === 1 && selectedItems[0].schema === 'group') {

      parent = {}
      parent.isGroup = true
      parent._id = selectedItems[0]._id
      parent.name = selectedItems[0].name
      parent.position = selectedItems[0].position

      isGroup = true

      const { group } = getGroupFromList(macro.result, selectedItems[0]._id, 0)

      if (!group) return

      const collapsedTasks = getCollapsedTasks(project_id, 'macro')

      childTasks = getGroupTasks(group.items, collapsedTasks)

    } else if (selectedItems.length === 1 && selectedItems[0].schema === 'task' && selectedItems[0].type === 'partida') {

      isItem = true

      hasSubTasks = selectedItems[0].hasSubTasks

      if (hasSubTasks) {

        childTasks = selectedItems[0].subTasks
      }

      parent = {}
      parent._id = selectedItems[0]._id
      parent.name = selectedItems[0].name
      parent.type = selectedItems[0].type
      parent.position = selectedItems[0].position
    }

    const options: IContextMenuOption[] = []

    if (isGroup) {
      options.push({
        label: t('MACRO_CONTEXT_MENU:NewTask'),
        icon: <Icon name="plus" fas fw />,
        action: () => createNewTask(parent),
        disabled: !canUpdateSelectedItems(!isGroup ? selectedItems : childTasks)
      })
      options.push({
        label: t('MACRO_CONTEXT_MENU:NewGroup'),
        icon: <Icon name="plus" fas fw />,
        action: () => createNewGroup(parent),
      })
    }

    if (isItem) {
      options.push({
        label: t('MACRO_CONTEXT_MENU:NewWork'),
        icon: <Icon name="plus" fas fw />,
        action: () => createNewTask(parent),
        disabled: !canUpdateSelectedItems(!isGroup ? selectedItems : childTasks)
      })
    }

    options.push({
      label: t('MACRO_CONTEXT_MENU:Complete'),
      icon: <Icon name="check" fas fw />,
      action: () => {

        handleCompleteMany(!isGroup ? selectedItems : childTasks)
      },
      disabled: !canUpdateSelectedItems(!isGroup ? selectedItems : childTasks)
                || (!isGroup && selectedItems.filter((item: any) => item.progress < 100).length === 0)
                || (isGroup && childTasks.filter((item: any) => item.progress < 100).length === 0)
    })

    options.push({
      label: t('MACRO_CONTEXT_MENU:Reschedule'),
      icon: <Icon name="calendario" style={{ fontSize: 20, verticalAlign: -4 }} />,
      action: () => {

        if (isGroup) {

          handleReScheduleItems(childTasks)
        } else {

          if (hasSubTasks) {

            handleReScheduleItems(childTasks)
          } else {

            handleReScheduleItems(selectedItems)
          }
        }
      },
      disabled: !canReScheduleSelectedItems(selectedItems)
                || (!isGroup && selectedItems.filter((item: any) => item.progress < 100).length === 0)
                || (isGroup && childTasks.filter((item: any) => item.progress < 100).length === 0)
    })

    options.push({
      label: t('MACRO_CONTEXT_MENU:AddRestrictions'),
      icon: <Icon name="restricciones" style={{ fontSize: 20, verticalAlign: -4 }} />,
      action: () => {

        handleAddRestrictionToMany(!isGroup ? selectedItems : childTasks)
      },
      disabled: !canUpdateSelectedItems(!isGroup ? selectedItems : childTasks)
                || (!isGroup && selectedItems.filter((item: any) => item.progress < 100).length === 0)
                || (isGroup && childTasks.filter((item: any) => item.progress < 100).length === 0)
    })

    options.push({
      label: t('MACRO_CONTEXT_MENU:AddResponsibles'),
      icon: <Icon name="responsables" style={{ fontSize: 20, verticalAlign: -4 }} />,
      action: () => {

        console.log('edit')
      },
      // disabled: !canUpdateSelectedItems(selectedItems)
      disabled: true
    })


    options.push({
      label: t('MACRO_CONTEXT_MENU:MoveIntoGroup'),
      icon: <Icon name="folder-open" fas fw />,
      action: () => {

        console.log('edit')
      },
      items: [
        {
          label: 'Grupo 1',
          action: () => {

            console.log('edit')
          },
          disabled: !canUpdateSelectedItems(selectedItems)
        },
        {
          label: 'Grupo 2',
          action: () => {

            console.log('edit')
          },
          disabled: !canUpdateSelectedItems(selectedItems)
        },
        {
          label: 'Grupo 3',
          action: () => {

            console.log('edit')
          },
          disabled: !canUpdateSelectedItems(selectedItems)
        },
      ],
      disabled: true
    })

    if (ability.can('delete', 'task')) {

        options.push({
          separator: true
        })

        options.push({
          label: t('MACRO_CONTEXT_MENU:Delete'),
          icon: <Icon name="trash" fas fw />,
          action: () => {

            console.log('delete')
          },
          // disabled: !ability.can('delete', 'task')
          disabled: true
        })
    }

    if (options.length) {

      showContextMenu(options)
    }
  }

  useEffect(() => {

    if (projectLoaded) {

      setMySettings(project.mySettings)
    }
  }, [ projectLoaded ])

  useEffect(() => {

    if (programmationLoaded) {

      if (macro.loadStatus === 'none') {

        loadMacro()
      }
    }

  }, [ programmationLoaded, macro.loadStatus ])

  // ganttItems
  useEffect(() => {

    if (macro.loadStatus !== 'loaded') return
    if (macro.loadGanttItemsStatus !== 'none') return

    const options: IGetItemsListOptions = {
      itemsList: macro.result,
      collapsedGroups: macro.collapsedGroups,
      collapsedTasks: macro.collapsedTasks,
      schema: 'macro',
      nonWorkingDays,
      stretchUpToday: true,
      moveToScheduledDays: mySettings?.moveToScheduledDays,
      showOnlyThisUserTasks: project.mySettings.showOnlyMyTasks ? userData?.id : undefined,
    }

    const ganttItems = getGanttItems(options);

    dispatch(setMacroGanttItems(ganttItems))
  }, [ macro.loadGanttItemsStatus, macro.result, mySettings ]);

  useEffect(() => {

    if (macro.result.length && task_id) {

      handleEditItem(task_id)
    }
  }, [ macro.result, task_id ])

  useEffect(() => {

    if (tooManyRestrictions) {

      setShownTooManyRestrictionsModal(false)

      showTooManyRestrictionsModal('Hay demasiadas restricciones en este proyecto', project, () => setShownTooManyRestrictionsModal(true))
    }

    if (tooManyTasks) {

      showTooManyTasksModal('Hay colaboradores con demasiadas tareas en este proyecto', project.user === userData?.id)
    }
  }, [ tooManyRestrictions, tooManyTasks ])

  useEffect(() => {

    if (!projectLoaded || !programmationLoaded) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }
  }, [ projectLoaded, programmationLoaded ])

  useEffect(() => {

    const columns: GanttColumn[] = [
      {
        label: t('MACRO:ColumnType'),
        field: 'type',
        width: 50,
        render: (field: any) => {

          if (!field) return null

          if (field.toLowerCase() === 'trabajo') return 'W'

          return field.substring(0, 1).toUpperCase()
        }
      },
      {
        label: t('MACRO:ColumnColorCode'),
        field: 'colorCodeName',
        width: 60,
      },
      {
        label: t('MACRO:ColumnPosition'),
        field: 'position',
        width: 100,
      },
      {
        label: t('MACRO:ColumnTitle'),
        field: 'name',
        width: 300,
      },
      {
        label: t('MACRO:ColumnStatus'),
        field: 'status',
        width: 100,
        render: (field: any) => getStatusName(field)
      },
      {
        label: t('MACRO:ColumnStartsAt'),
        field: 'startsAt',
        width: 80,
        render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
      },
      {
        label: t('MACRO:ColumnEndsAt'),
        field: 'endsAt',
        width: 80,
        render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
      },
      {
        label: t('MACRO:ColumnResponsibles'),
        field: 'responsibles',
        width: 80,
        render: (field: any, item: any) => {

          const isField = !!field
          const isFieldArray = isField && Array.isArray(field)

          if (isField && isFieldArray) {

            const responsibles = field.filter((user: any) => {

              if (!user || typeof user === 'string') return false

              return teamsMembersIds.includes(user._id)
            })

            return responsibles.map((user: any) => <UserAvatar key={user._id} user={user} size="xs" />)
          }

          return null
        }
      },
      {
        label: t('MACRO:ColumnStartedAt'),
        field: 'startedAt',
        width: 80,
        render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
      },
      {
        label: t('MACRO:ColumnEndedAt'),
        field: 'endedAt',
        width: 80,
        render: (field: any) => field ? moment(field).format('DD/MM/YYYY') : null
      },
    ]

    setColumns(columns)
  }, [teams, teamsMembersIds])

  return (
    <ProjectLayout
      menuName="programmation"
      subMenuName="macro"
      hideSubMenu
    >
      <ProgrammationHeader
        activeTab="macro"
        projectId={project_id}
        subtitle={`${t('MACRO:Title')} · ${project.name || ''}`}
      >
        <ResponsivePageToolbar
          className="pl4-programmation-toolbar"
          actionsClassName="pl4-programmation-toolbar__actions"
          primaryAction={ability.can('create', 'task') ? (
              <ProgrammationCreateMenu
                importDisabled={macro.ganttItems.length > 0 || hasActiveFilters()}
                onCreateTask={() => createNewTask()}
                onCreateGroup={() => createNewGroup()}
              />
          ) : undefined}
          actions={[
            {
              id: 'filter',
              label: t('MACRO:ToolbarFilterButton'),
              icon: <span className="icon-filtro" aria-hidden="true" />,
              onClick: () => setShowTaskFilters(true),
              active: hasActiveFilters(),
              className: 'pl4-programmation-action',
            },
            {
              id: 'export',
              label: t('WEEKLY:ExportButton'),
              icon: <i className="fas fa-download" aria-hidden="true" />,
              onClick: () => setShowPdfExportModal(true),
              className: 'pl4-programmation-action',
            },
            {
              id: 'settings',
              label: t('MACRO:ToolbarSettingsButton'),
              icon: <span className="icon-configuracion" aria-hidden="true" />,
              onClick: () => toggleSidebar(),
              active: showSidebar,
              buttonRef: sidebarRefBtn,
              className: 'pl4-programmation-action pl4-settings-trigger',
              mobileClassName: 'pl4-settings-trigger',
            },
          ]}
        />
      </ProgrammationHeader>
      <ContentBody noScroll>
        <Card className='table-card' style={{ height: 'calc(100% - 8px)' }}>
          {project._id && (
            <Gantt
              projectId={project._id}
              settingsId={project.mySettings?._id}
              schema="macro"
              start={project.startsAt}
              end={maxDate(project.endsAt, projectedEndDate) || project.endsAt}
              items={macro.ganttItems}
              columns={columns}
              nonWorkingDays={extendedNonWorkingDays}
              dayWidth={mySettings?.ganttMacroColumnSize || 40}
              rowHeight={32}
              maxRowHeight={32}
              hideNonWorkingDays={mySettings?.ganttMacroHideNonWorkingDays || false}
              showVerticalBorders={mySettings?.ganttMacroShowVerticalBorders || false}
              useCodeColors={mySettings?.useCodeColors || false}
              stretchUpToday={true}
              moveToScheduledDays={mySettings?.moveToScheduledDays || false}
              columnsHidden={mySettings?.ganttHideMacroFields || []}
              divisorPosition={mySettings?.ganttMacroDivisorPosition}
              showSidebar={showSidebar}
              onSelectItem={handleEditItem}
              onToggleCollapse={handleToggleCollapse}
              onToggleCollapseTask={handleToggleCollapseTask}
              onShowWorkflows={handleShowWorkflows}
              onClickOutside={toggleSidebar}
              onUpdateSetting={handleUpdateSetting}
              onUpdateOrder={handleUpdatePosition}
              sortEnabled={me?.profile_tag !== 'observer'}
              lineBaseTasks={mySettings?.lineBaseTasks }
              onItemsRightClick={handleItemsRightClick}
              onGanttRightClick={handleGanttRightClick}
              onResetBaseLines={handleResetBaseLines}
              onResetActualDates={handleResetActualDates}
            />
          )}
        </Card>
      </ContentBody>

      <ProgrammationManager taskList={macro.result} />

      <TaskFilters
        show={showTaskFilters}
        type="macro"
        filters={filters}
        onChange={handleChangeFilters}
        onHide={() => setShowTaskFilters(false)}
      />

      <AddRestrictionToMany
        show={showAddRestrictionModal}
        tasks={restrictionTasks}
        onCancel={() => setShowAddRestrictionModal(false)}
        onSaved={() => onRestrictionAdded()}
      />

      <ReScheduleMany
        show={showReScheduleModal}
        tasks={reScheduleTasks}
        nonWorkingDays={nonWorkingDays}
        onCancel={() => {

          setReScheduleTasks([])
          setShowReScheduleModal(false)
        }}
        onSaved={() => onReScheduledTasks()}
      />

      <PdfExportModal
        show={showPdfExportModal}
        plan="macro"
        project_id={showPdfExportModal ? project_id : undefined}
        items={macro.ganttItems}
        nonWorkingDays={nonWorkingDays}
        users={programmation.users}
        filters={filters}
        columns={columns.map((column: any) => {

          let key = column.field

          if (key === 'position') {
            key = 'id'
          } else if (key === 'name') {
            key = 'title'
          }

          return {
            key,
            label: column.label,
            width: column.width
          }
        })}
        collapsedGroups={macro.collapsedGroups}
        collapsedTasks={macro.collapsedTasks}
        onHide={() => setShowPdfExportModal(false)}
      />
    </ProjectLayout>
  )
}

export default MacroPage
